import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  Button,
  TextField,
  Input
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

class SearchableMultiSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: '',
    };
  }

  handleSearchChange = (event) => {
    event.stopPropagation();
    this.setState({ searchText: event.target.value });
  };

  clearSearch = () => {
    this.setState({ searchText: '' });
  };

  filterItems = (items, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return items;
    }
    const lowerSearchText = searchText.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(lowerSearchText)
    );
  };

  highlightText = (text, searchText) => {
    if (!searchText || searchText.trim() === '') {
      return <span>{text}</span>;
    }
    
    const lowerText = text.toLowerCase();
    const lowerSearchText = searchText.toLowerCase();
    const startIndex = lowerText.indexOf(lowerSearchText);
    
    if (startIndex === -1) {
      return <span>{text}</span>;
    }
    
    const endIndex = startIndex + searchText.length;
    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(startIndex, endIndex);
    const afterMatch = text.substring(endIndex);
    
    return (
      <span>
        {beforeMatch}
        <span style={{ 
          backgroundColor: '#e0e0e0',
          fontWeight: '500',
          padding: '2px 4px',
          borderRadius: '3px'
        }}>
          {match}
        </span>
        {afterMatch}
      </span>
    );
  };

  handleDeleteChip = (chipToDelete) => (event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (this.props.onDeleteChip) {
      this.props.onDeleteChip(chipToDelete)(event);
    }
  };

  handleClearAll = (event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (this.props.onClearAll) {
      this.props.onClearAll(event);
    }
  };

  render() {
    const {
      label,
      items = [],
      selectedChips = [],
      selectedItems = [],
      onChange,
      placeholder = "Search...",
      id,
      disabled = false,
      fullWidth = true,
      className,
      sx,
      variant = "standard"
    } = this.props;

    const { searchText } = this.state;
    const filteredItems = this.filterItems(items, searchText);
    const selectedIds = new Set(selectedItems.map(x => x.id));

    const isAllSelected = selectedChips.length > 0 && 
                          selectedChips[0] && 
                          selectedChips[0].id === '-1';

    const displayText = isAllSelected
      ? `All ${label.toLowerCase()}s selected`
      : selectedItems.length > 0
        ? `${selectedItems.length} of ${items.length - 1} selected`
        : `No ${label.toLowerCase()}s selected`;

    return (
      <FormControl 
        sx={{ width: fullWidth ? '100%' : 'auto', ...sx }} 
        className={className}
        disabled={disabled}
      >
        <InputLabel sx={{ ml: '-4%' }} htmlFor={id}>
          {label}
        </InputLabel>
        <Select
          multiple
          value={selectedChips}
          onChange={onChange}
          input={<Input id={id} />}
          renderValue={selected => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map(value => (
                <Chip 
                  key={value.id} 
                  label={value.name}
                  onDelete={this.handleDeleteChip(value)}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                  size="small"
                />
              ))}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
              },
            },
            autoFocus: false,
          }}
          onClose={this.clearSearch}
          variant={variant}
        >
          <MenuItem 
            disableRipple 
            onKeyDown={(e) => e.stopPropagation()}
            style={{ 
              position: 'sticky', 
              top: 0, 
              backgroundColor: 'white', 
              zIndex: 1,
              borderBottom: '1px solid #e0e0e0',
              padding: '8px 16px',
              cursor: 'default'
            }}
          >
            <TextField
              size="small"
              placeholder={placeholder}
              fullWidth
              value={searchText}
              onChange={this.handleSearchChange}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              InputProps={{
                startAdornment: (
                  <span role="img" aria-label="search" style={{ marginRight: 8, color: '#757575' }}>🔍</span>
                ),
                endAdornment: searchText && (
                  <span 
                    role="button"
                    aria-label="clear search"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState({ searchText: "" });
                    }}
                    style={{ 
                      cursor: 'pointer', 
                      color: '#757575',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      padding: '0 4px'
                    }}
                  >
                    ×
                  </span>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  }
                }
              }}
            />
          </MenuItem>
          <MenuItem 
            disableRipple
            style={{
              position: 'sticky',
              top: 56,
              backgroundColor: 'white',
              zIndex: 1,
              borderBottom: '1px solid #e0e0e0',
              padding: '8px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'default'
            }}
          >
            <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
              {displayText}
            </span>
            {selectedItems.length > 0 && (
              <Button
                size="small"
                onClick={this.handleClearAll}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  minWidth: 'auto',
                  padding: '4px 8px',
                  color: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#ffebee'
                  }
                }}
              >
                Clear All
              </Button>
            )}
          </MenuItem>
          {filteredItems.map(item => (
            <MenuItem key={item.id} value={item}>
              <Checkbox checked={selectedIds.has(item.id)} />
              <ListItemText 
                primary={this.highlightText(item.name, searchText)} 
              />
            </MenuItem>
          ))}
          {filteredItems.length === 0 && (
            <MenuItem disabled style={{ justifyContent: 'center', color: '#757575' }}>
              No {label.toLowerCase()}s found
            </MenuItem>
          )}
        </Select>
      </FormControl>
    );
  }
}

SearchableMultiSelect.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  selectedChips: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onDeleteChip: PropTypes.func,
  onClearAll: PropTypes.func,
  placeholder: PropTypes.string,
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  sx: PropTypes.object,
  variant: PropTypes.string,
};

export default SearchableMultiSelect;




// WEBPACK FOOTER //
// ./src/components/common/SearchableMultiSelect.js